import { Graphics, Text, Container } from 'pixi.js';
import { Scene } from '../core/Scene';
import { assetLoader } from '../core/AssetLoader';
import { COLORS } from '../config/constants';
import { emitGameEvent } from '../core/GameEvents';
import { preloadShipSVGs } from '../entities/ShipGraphics';
import { preloadStationSVGs } from '../entities/Station';
import { preloadDroneSVGs } from '../entities/Drone';

/**
 * BootScene - Loads assets and generates textures before starting the game
 */
export class BootScene extends Scene {
  private progressBar!: Graphics;
  private progressBox!: Graphics;
  private loadingText!: Text;
  private progressContainer!: Container;

  constructor(key: string) {
    super(key);
  }

  async create(): Promise<void> {
    // Welcome message
    console.log(
      '%c ASTRA %c Space awaits, pilot.',
      'background: #20a5a6; color: #0a1520; font-weight: bold; padding: 4px 8px; font-size: 14px;',
      'color: #20a5a6; padding: 4px 0; font-size: 14px;'
    );

    const width = this.app.screen.width;
    const height = this.app.screen.height;

    // Create loading UI container (centered)
    this.progressContainer = new Container();
    this.progressContainer.position.set(width / 2, height / 2);
    this.addChild(this.progressContainer);

    // Progress box background
    this.progressBox = new Graphics();
    this.progressBox.rect(-160, -25, 320, 50);
    this.progressBox.fill({ color: 0x1a2a38, alpha: 0.8 });
    this.progressContainer.addChild(this.progressBox);

    // Progress bar (filled as loading progresses)
    this.progressBar = new Graphics();
    this.progressContainer.addChild(this.progressBar);

    // Loading text
    this.loadingText = new Text({
      text: 'INITIALIZING ASTRA...',
      style: {
        fontFamily: 'monospace',
        fontSize: 20,
        fill: '#dd5639',
      },
    });
    this.loadingText.anchor.set(0.5);
    this.loadingText.position.set(0, -50);
    this.progressContainer.addChild(this.loadingText);

    // Queue assets for loading
    this.queueAssets();

    // Load assets with progress
    await assetLoader.load((progress) => {
      this.updateProgress(progress.progress);
      emitGameEvent('loadingProgress', {
        progress: progress.progress * 0.5, // First 50% for file loading
        message: 'Loading assets...',
      });
    });

    // Preload ship SVGs
    this.updateProgress(0.5);
    emitGameEvent('loadingProgress', {
      progress: 0.5,
      message: 'Loading ship graphics...',
    });

    await preloadShipSVGs();
    await preloadStationSVGs();
    await preloadDroneSVGs();

    // Generate procedural textures
    this.updateProgress(0.7);
    emitGameEvent('loadingProgress', {
      progress: 0.7,
      message: 'Generating textures...',
    });

    await this.generatePlaceholderAssets();

    this.updateProgress(0.9);
    emitGameEvent('loadingProgress', {
      progress: 0.9,
      message: 'Connecting to server...',
    });

    // Clean up loading UI
    this.progressContainer.destroy({ children: true });

    // Start the game scene (gameReady is emitted once player data arrives from server)
    await this.sceneManager.start('GameScene');
  }

  private queueAssets(): void {
    // Background textures
    assetLoader
      .addImage('bg_layer_1', 'assets/backgrounds/bg_layer_1.png')
      .addImage('bg_layer_2', 'assets/backgrounds/bg_layer_2.png')
      .addImage('bg_layer_3', 'assets/backgrounds/bg_layer_3.png')
      .addImage('nebula_cloud', 'assets/backgrounds/nebula.png');

    // Sprites
    assetLoader
      .addImage('gas_pocket_svg', 'assets/sprites/gas.svg');

    // Audio is now managed by AudioManager (initialized in GameScene)
  }

  private updateProgress(value: number): void {
    this.progressBar.clear();
    this.progressBar.rect(-150, -15, 300 * value, 30);
    this.progressBar.fill({ color: COLORS.UI_PRIMARY });
  }

  private async generatePlaceholderAssets(): Promise<void> {
    // Background star particle
    const starGraphics = new Graphics();
    starGraphics.rect(0, 0, 2, 2);
    starGraphics.fill({ color: 0xffffff });
    assetLoader.generateTexture(starGraphics, 'star');
    starGraphics.destroy();

    // Bullet/projectile (teal with cream core)
    const bulletGraphics = new Graphics();
    bulletGraphics.rect(0, 0, 4, 12);
    bulletGraphics.fill({ color: 0x20a5a6 });
    bulletGraphics.rect(1, 0, 2, 4);
    bulletGraphics.fill({ color: 0xf5e8d1 });
    assetLoader.generateTexture(bulletGraphics, 'bullet');
    bulletGraphics.destroy();

    // Mining laser beam (orange)
    const laserGraphics = new Graphics();
    laserGraphics.rect(0, 0, 2, 100);
    laserGraphics.fill({ color: 0xdd5639, alpha: 0.8 });
    assetLoader.generateTexture(laserGraphics, 'mining_laser');
    laserGraphics.destroy();

    // Resource pickup particles
    const resourceColors = [0x8b8b8b, 0xb87333, 0xffd700, 0x20a5a6];
    for (let i = 0; i < resourceColors.length; i++) {
      const resourceGraphics = new Graphics();
      resourceGraphics.circle(4, 4, 4);
      resourceGraphics.fill({ color: resourceColors[i] });
      assetLoader.generateTexture(resourceGraphics, `resource_${i}`);
      resourceGraphics.destroy();
    }

    // Small delay to let textures process
    await new Promise((resolve) => setTimeout(resolve, 50));
  }

  update(_delta: number): void {
    // BootScene doesn't need update logic
  }

  shutdown(): void {
    // Clean up if needed
  }
}
